import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import { IconX, IconCheck, IconClock, IconEyeCheck, IconAlertCircle, IconHourglass } from '@tabler/icons';
import api from '../../../../services/api';

const CAMPAIGN_STATUS_LABELS = {
    draft: 'Rascunho',
    scheduled: 'Agendada',
    processing: 'Enviando...',
    completed: 'Concluída',
    failed: 'Falhou'
};

const CAMPAIGN_STATUS_COLORS = {
    draft: 'default',
    scheduled: 'warning',
    processing: 'info',
    completed: 'success',
    failed: 'error'
};

const RECIPIENT_STATUS_LABELS = {
    pending: 'Pendente',
    processing: 'Enviando',
    sent: 'Enviado',
    failed: 'Falhou',
    skipped: 'Pulado (opt-out)'
};

const RECIPIENT_STATUS_COLORS = {
    pending: 'default',
    processing: 'info',
    sent: 'success',
    failed: 'error',
    skipped: 'default'
};

const PAGE_SIZE = 30;

// Enquanto a campanha está ativa (mandando de verdade ou esperando a data
// agendada chegar), repolla pra os números subirem sozinhos sem precisar
// de F5 - mesmo padrão de polling+useRef já usado no Pipeline/Chats.
const POLLING_STATUSES = ['processing', 'scheduled'];
const POLL_INTERVAL_MS = 5000;

const StatCard = ({ label, value, icon, color }) => (
    <Paper sx={{ p: 2, borderRadius: '16px', textAlign: 'center' }}>
        <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
    </Paper>
);

// Dashboard de uma campanha - números reais (`sent_count`/`failed_count`/
// `delivered_count`/`read_count`, já incrementados de verdade pelo envio
// e pelos webhooks de status da Meta/w-api) + lista paginada de
// destinatários com o status individual de cada um.
const CampaignDetailDialog = ({ open, onClose, appId, campaignId }) => {
    const [campaign, setCampaign] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [recipientsTotal, setRecipientsTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const loadedCountRef = useRef(PAGE_SIZE);

    const loadCampaign = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/marketing/campaigns/${campaignId}/`);
            setCampaign(res.data?.campanha || null);
        } catch (error) {
            console.error('Erro ao carregar campanha', error);
        }
    };

    const loadRecipients = async (limit = loadedCountRef.current) => {
        try {
            const res = await api.get(
                `v1/apps/${appId}/marketing/campaigns/${campaignId}/recipients_status/?limit=${limit}&offset=0`
            );
            setRecipients(res.data?.results || []);
            setRecipientsTotal(res.data?.total || 0);
            loadedCountRef.current = Math.max(limit, (res.data?.results || []).length);
        } catch (error) {
            console.error('Erro ao carregar destinatários', error);
        }
    };

    useEffect(() => {
        if (!open || !appId || !campaignId) return;

        loadedCountRef.current = PAGE_SIZE;
        setLoading(true);
        Promise.all([loadCampaign(), loadRecipients(PAGE_SIZE)]).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, appId, campaignId]);

    useEffect(() => {
        if (!open || !campaign || !POLLING_STATUSES.includes(campaign.status)) return;
        const interval = setInterval(() => {
            loadCampaign();
            loadRecipients();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, campaign?.status]);

    const handleLoadMore = async () => {
        setLoadingMore(true);
        await loadRecipients(loadedCountRef.current + PAGE_SIZE);
        setLoadingMore(false);
    };

    if (!campaign && loading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                    <CircularProgress />
                </Box>
            </Dialog>
        );
    }

    if (!campaign) return null;

    const total = campaign.total_recipients || 0;
    const sent = campaign.sent_count || 0;
    const failed = campaign.failed_count || 0;
    const delivered = campaign.delivered_count || 0;
    const read = campaign.read_count || 0;
    const pending = Math.max(total - sent - failed, 0);
    const progressPercent = total > 0 ? Math.min(100, Math.round(((sent + failed) / total) * 100)) : 0;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {campaign.title}
                    </Typography>
                    <Chip
                        size="small"
                        label={CAMPAIGN_STATUS_LABELS[campaign.status] || campaign.status}
                        color={CAMPAIGN_STATUS_COLORS[campaign.status] || 'default'}
                        sx={{ mt: 0.5 }}
                    />
                </Box>
                <IconButton onClick={onClose}>
                    <IconX />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Progresso do envio</Typography>
                        <Typography variant="body2">{progressPercent}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={progressPercent} sx={{ height: 10, borderRadius: '999px' }} />
                </Box>

                <Grid container spacing={2} sx={{ my: 2 }}>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <StatCard label="Enviados" value={sent} icon={<IconCheck size={22} />} color="#2563eb" />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <StatCard label="Entregues" value={delivered} icon={<IconCheck size={22} />} color="#0891b2" />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <StatCard label="Lidos" value={read} icon={<IconEyeCheck size={22} />} color="#16a34a" />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <StatCard label="Falharam" value={failed} icon={<IconAlertCircle size={22} />} color="#dc2626" />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <StatCard label="Pendentes" value={pending} icon={<IconHourglass size={22} />} color="#a16207" />
                    </Grid>
                </Grid>

                {(delivered > 0 || read > 0) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Entregue/lido só é confirmado quando a Meta/API NÃO OFICIAL manda o status de volta - pode
                        demorar um pouco a aparecer, e nem todo provedor confirma isso de forma confiável
                        (principalmente API não-oficial).
                    </Typography>
                )}

                <Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
                    Destinatários ({recipientsTotal})
                </Typography>

                <Stack spacing={1}>
                    {recipients.map((r) => (
                        <Paper
                            key={r.id}
                            sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '12px' }}
                        >
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {r.name || '(sem nome)'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {r.phone || r.email}
                                </Typography>
                                {r.status === 'failed' && r.error_message && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                        {r.error_message}
                                    </Typography>
                                )}
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {r.read_at ? (
                                    <Chip size="small" label="Lido" color="success" icon={<IconEyeCheck size={14} />} />
                                ) : r.delivered_at ? (
                                    <Chip size="small" label="Entregue" color="info" icon={<IconCheck size={14} />} />
                                ) : null}
                                <Chip
                                    size="small"
                                    label={RECIPIENT_STATUS_LABELS[r.status] || r.status}
                                    color={RECIPIENT_STATUS_COLORS[r.status] || 'default'}
                                />
                            </Stack>
                        </Paper>
                    ))}
                    {recipients.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Nenhum destinatário ainda.
                        </Typography>
                    )}
                </Stack>

                {recipients.length < recipientsTotal && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            startIcon={loadingMore ? <CircularProgress size={16} /> : <IconClock size={16} />}
                            sx={{ textTransform: 'none' }}
                        >
                            Carregar mais ({recipientsTotal - recipients.length} restantes)
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default CampaignDetailDialog;
